import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { UserCircle, SealCheck, Flower, Users } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import AddPersonDialog from "@/components/AddPersonDialog";

const FILTERS = [
  { v: "all", label: "All" },
  { v: "current", label: "Current" },
  { v: "former", label: "Former" },
  { v: "retired", label: "Retired" },
  { v: "deceased", label: "In Memoriam" },
  { v: "teaching", label: "Teaching" },
  { v: "non_teaching", label: "Non-Teaching" },
  { v: "leadership", label: "Leadership" },
];

// Institution "People" directory — teachers, staff & faculty (Phase 1).
export function InstitutionPeople({ institutionId, institutionName, canManage }) {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);

  const load = () => api.get(`/institutions/${institutionId}/people`, { params: { filter } })
    .then((r) => setData(r.data)).catch(() => setData({ total: 0, people: [], counts: {} }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [institutionId, filter]);

  return (
    <div data-testid="institution-people">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-xl font-medium">People</h3>
          <p className="text-sm text-muted-foreground">Teachers, faculty & staff who built this institution — living and remembered.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/find-teacher" className="text-sm font-medium text-primary hover:underline" data-testid="find-teacher-link">Find my teacher →</Link>
          <AddPersonDialog institutionId={institutionId} institutionName={institutionName} onCreated={() => load()} />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="people-filters">
        {FILTERS.map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`people-filter-${f.v}`}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${filter === f.v ? "border-secondary bg-secondary text-secondary-foreground" : "border-border bg-background text-muted-foreground hover:border-secondary/50"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {data === null ? <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
        : data.people.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-border/60 p-8 text-center">
            <Users size={28} className="mx-auto text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No people added yet for this filter. Be the first to honour a teacher or staff member.</p>
          </div>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.people.map((pp) => (
              <Link key={pp.id} to={`/person/${pp.id}`} data-testid={`people-card-${pp.id}`}
                className="group flex items-start gap-3 rounded-xl border border-slate-100 bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-green-300 hover:shadow-md">
                <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg bg-gradient-to-br from-green-50 to-orange-50">
                  {pp.photo_url ? <img src={pp.photo_url} alt="" className="h-full w-full object-cover" /> : <UserCircle size={24} weight="duotone" className="text-green-600" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1 text-sm font-medium text-foreground group-hover:text-primary">
                    {pp.name}
                    {pp.status === "deceased" && <Flower size={13} weight="fill" className="shrink-0 text-primary" title="In Memoriam" />}
                    {(pp.verification_status === "self_verified" || pp.verification_status === "family_verified") && <SealCheck size={12} weight="fill" className="shrink-0 text-primary" />}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">{pp.role_here || pp.title || pp.subcategory}</span>
                  {pp.status === "deceased" && <Badge variant="outline" className="mt-1 rounded-full border-primary/20 bg-primary/5 text-[10px] font-normal text-primary">In Memoriam</Badge>}
                </span>
              </Link>
            ))}
          </div>
        )}
    </div>
  );
}

export default InstitutionPeople;
